<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert, useTrack } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useDebounceFn } from '@vueuse/core';
import { HUBPOST_CARS_ONBOARDING_EVENTS } from 'dashboard/helper/AnalyticsHelper/wgptEvents';
import { slugErrorMessage } from 'dashboard/helper/wgpt/showroomSlug';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';
import OnboardingLayout from '../../../shared/OnboardingLayout.vue';
import OnboardingSection from '../../../shared/OnboardingSection.vue';
import OnboardingFormRow from '../../../account-details/OnboardingFormRow.vue';
import OnboardingFormSelect from '../../../account-details/OnboardingFormSelect.vue';
import BackgroundPicker from './BackgroundPicker.vue';

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const { accountId, currentAccount, finishOnboarding } = useAccount();

const currentUser = useMapGetter('getCurrentUser');
const showroom = useMapGetter('wgptCarShowroom/getShowroom');

const slug = ref('');
const defaultLocale = ref('en');
const backgroundPreset = ref('');

const contact = reactive({
  contact_phone: '',
  contact_email: '',
  contact_whatsapp: '',
  location_text: '',
});

// Usernames only — the storefront builds the profile URL from a fixed prefix
// per platform (WgptCarShowroom::SOCIAL_PLATFORM_URLS).
const SOCIAL_PLATFORMS = ['facebook', 'instagram', 'tiktok', 'snapchat', 'x'];
const socialLinks = reactive(
  Object.fromEntries(SOCIAL_PLATFORMS.map(platform => [platform, '']))
);

const SOCIAL_ICONS = {
  facebook: 'i-ri-facebook-circle-fill',
  instagram: 'i-ri-instagram-line',
  tiktok: 'i-ri-tiktok-fill',
  snapchat: 'i-ri-snapchat-fill',
  x: 'i-ri-twitter-x-fill',
};

const CONTACT_FIELDS = [
  { key: 'contact_phone', icon: 'i-lucide-phone', type: 'tel' },
  { key: 'contact_whatsapp', icon: 'i-lucide-message-circle', type: 'tel' },
  { key: 'contact_email', icon: 'i-lucide-mail', type: 'email' },
  { key: 'location_text', icon: 'i-lucide-map-pin', type: 'text' },
];

const logoInput = ref(null);
const isUploadingLogo = ref(false);
const isSubmitting = ref(false);

// Why the address was rejected, ready to show under the field. Set from the
// availability check as the dealer types, and from the server if a submit is
// still rejected — an empty string means the address is fine so far.
const slugError = ref('');

// Where the address currently in the field would put the storefront. Comes back
// from the same check, because the URL shape (subdomain vs path) is the
// server's to know. Empty until the first check answers.
const slugPreviewUrl = ref('');

const rules = { slug: { required }, backgroundPreset: { required } };

const v$ = useVuelidate(rules, { slug, backgroundPreset });

// A toast alone doesn't say *which* field is missing. Fields carry a persistent
// red outline once touched-and-invalid, and shake once on a rejected submit to
// pull the eye to them.
const showErrorOnFields = ref(false);

const fieldClass = (field, invalid = false) => [
  'w-auto text-end px-1 py-0.5 -my-0.5 mx-0 placeholder:text-n-slate-9 rounded',
  {
    'ring-1 ring-n-ruby-8': v$.value[field].$error || invalid,
    'animate-shake':
      showErrorOnFields.value && (v$.value[field].$error || invalid),
  },
];

const flagMissingFields = () => {
  showErrorOnFields.value = true;
  setTimeout(() => {
    showErrorOnFields.value = false;
  }, 600);
};

const accountName = computed(() => currentAccount.value?.name || '');
const userName = computed(() => currentUser.value?.name || '');
const logoUrl = computed(() => showroom.value?.logo_url || '');

// The address the storefront would be reachable at: what the dealer is typing
// once it has been checked, and where it lives today until then.
const publicUrl = computed(
  () => slugPreviewUrl.value || showroom.value?.public_url || ''
);

const localeOptions = computed(() => [
  { value: 'en', label: t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.LOCALES.en') },
  { value: 'ar', label: t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.LOCALES.ar') },
]);

// Carried on both step events so the funnel can be read per market.
const region = computed(() => currentAccount.value?.wgpt_attributes?.region);

// How much of the storefront the dealer actually filled in. Counts and flags
// rather than the values themselves — the contact details are the dealership's,
// and the social handles are theirs to publish, not ours to ship to Amplitude.
const completedFields = () => ({
  region: region.value,
  default_locale: defaultLocale.value,
  background_preset: backgroundPreset.value,
  has_logo: Boolean(logoUrl.value),
  contact_fields_filled: Object.values(contact).filter(Boolean).length,
  social_links_filled: Object.values(socialLinks).filter(Boolean).length,
});

onMounted(async () => {
  useTrack(HUBPOST_CARS_ONBOARDING_EVENTS.SHOWROOM_SETUP_VISITED, {
    region: region.value,
  });

  // The storefront was created at signup, so this step edits an existing
  // record — prefill from it rather than starting blank.
  const record = await store.dispatch('wgptCarShowroom/get');
  slug.value = record?.slug || '';
  defaultLocale.value = record?.default_locale || 'en';
  Object.keys(contact).forEach(key => {
    contact[key] = record?.[key] || '';
  });
  SOCIAL_PLATFORMS.forEach(platform => {
    socialLinks[platform] = record?.social_links?.[platform] || '';
  });
});

// WgptSite owns the address rules, so the server is asked which one a candidate
// breaks rather than the rules being restated here. Returns the message so the
// submit path can run the same check without waiting on the debounce.
const verifySlug = async value => {
  // A check that never came back must not block the dealer: the save validates
  // the address again anyway.
  const check = await store
    .dispatch('wgptCarShowroom/checkSlug', value)
    .catch(() => null);
  slugError.value = slugErrorMessage(t, check);
  slugPreviewUrl.value = check?.preview_url || '';
  return slugError.value;
};

const checkSlug = useDebounceFn(value => verifySlug(value), 400);

watch(slug, value => {
  // Clear as they type: an error about the address they just fixed is worse
  // than no error until the check comes back. The preview holds its last value
  // instead, so the line doesn't blink out between keystrokes.
  slugError.value = '';
  if (value) {
    checkSlug(value);
  } else {
    slugPreviewUrl.value = '';
  }
});

const onLogoSelected = async event => {
  const file = event.target.files?.[0];
  if (!file) return;
  isUploadingLogo.value = true;
  try {
    await store.dispatch('wgptCarShowroom/attachImage', { kind: 'logo', file });
  } catch {
    useAlert(t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.LOGO.ERROR'));
  } finally {
    isUploadingLogo.value = false;
    event.target.value = '';
  }
};

const handleSubmit = async () => {
  if (isSubmitting.value) return;
  v$.value.$touch();
  if (v$.value.$invalid) {
    useAlert(t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.VALIDATION_ERROR'));
    flagMissingFields();
    return;
  }

  // Re-check rather than trusting the debounced result: a fast submit can land
  // before the last keystroke was ever checked.
  const addressError = await verifySlug(slug.value);
  if (addressError) {
    useAlert(addressError);
    flagMissingFields();
    return;
  }

  isSubmitting.value = true;
  try {
    await finishOnboarding({
      onboarding_step: 'wgpt_hubpost_cars_showroom_setup',
      showroom: {
        slug: slug.value,
        default_locale: defaultLocale.value,
        background_preset: backgroundPreset.value,
        ...contact,
        social_links: { ...socialLinks },
      },
    });

    // Only after the step actually persisted — a validation bounce or a failed
    // request must not read as a completed storefront.
    useTrack(
      HUBPOST_CARS_ONBOARDING_EVENTS.SHOWROOM_SETUP_COMPLETED,
      completedFields()
    );

    useAlert(t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.SUCCESS'));
    router.push({
      name: 'onboarding_inbox_setup',
      params: { accountId: accountId.value },
    });
  } catch (error) {
    // Validation bounces come back as { message, attributes }. Anything naming
    // the address belongs under that field rather than only in a toast — and
    // since the server phrases it in English, re-run our own check first so the
    // dealer reads it in their language.
    const serverMessage = parseAPIErrorResponse(error);
    if ((error?.response?.data?.attributes || []).includes('slug')) {
      slugError.value = (await verifySlug(slug.value)) || serverMessage;
      flagMissingFields();
      useAlert(slugError.value);
      return;
    }
    useAlert(
      typeof serverMessage === 'string'
        ? serverMessage
        : t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <form @submit.prevent="handleSubmit">
    <OnboardingLayout
      :greeting="
        t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.GREETING', { name: userName })
      "
      :subtitle="t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.SUBTITLE')"
      :continue-label="t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.CONTINUE')"
      :is-loading="isSubmitting"
    >
      <OnboardingSection
        :title="t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.SHOWROOM.TITLE')"
        icon="i-lucide-store"
      >
        <div class="flex items-center gap-2 px-3 py-3">
          <img
            v-if="logoUrl"
            :src="logoUrl"
            :alt="accountName"
            class="object-contain size-5"
          />
          <span class="text-sm font-medium text-n-slate-12">
            {{ accountName }}
          </span>
        </div>
        <OnboardingFormRow
          :title="t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.SHOWROOM.ADDRESS')"
          icon="i-lucide-link"
        >
          <div class="flex items-center justify-end gap-1">
            <InlineInput
              v-model="slug"
              :placeholder="
                t(
                  'WGPT_HUBPOST_CARS_SHOWROOM_SETUP.SHOWROOM.ADDRESS_PLACEHOLDER'
                )
              "
              :custom-input-class="fieldClass('slug', Boolean(slugError))"
              @blur="v$.slug.$touch()"
            />
          </div>
        </OnboardingFormRow>
        <div v-if="slugError" class="px-3 pb-2 text-sm text-end text-n-ruby-11">
          {{ slugError }}
        </div>
        <div
          v-else-if="publicUrl"
          class="px-3 pb-2 text-sm text-end text-n-slate-10"
        >
          {{ publicUrl }}
        </div>
        <OnboardingFormRow
          :title="t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.SHOWROOM.LOGO')"
          icon="i-lucide-image"
        >
          <div class="flex items-center justify-end gap-2">
            <NextButton
              type="button"
              slate
              outline
              xs
              :is-loading="isUploadingLogo"
              :label="
                logoUrl
                  ? t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.SHOWROOM.LOGO_REPLACE')
                  : t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.SHOWROOM.LOGO_UPLOAD')
              "
              @click="logoInput.click()"
            />
            <input
              ref="logoInput"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              class="hidden"
              @change="onLogoSelected"
            />
          </div>
        </OnboardingFormRow>
        <OnboardingFormRow
          :title="t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.SHOWROOM.LANGUAGE')"
          icon="i-lucide-languages"
        >
          <OnboardingFormSelect
            v-model="defaultLocale"
            :options="localeOptions"
          />
        </OnboardingFormRow>
        <div class="px-3 pt-3 text-sm text-n-slate-11">
          {{ t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.SHOWROOM.BACKGROUND') }}
        </div>
        <BackgroundPicker v-model="backgroundPreset" />
      </OnboardingSection>

      <OnboardingSection
        :title="t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.CONTACT.TITLE')"
        icon="i-lucide-contact"
      >
        <OnboardingFormRow
          v-for="field in CONTACT_FIELDS"
          :key="field.key"
          :title="t(`WGPT_HUBPOST_CARS_SHOWROOM_SETUP.CONTACT.${field.key}`)"
          :icon="field.icon"
        >
          <InlineInput
            v-model="contact[field.key]"
            :type="field.type"
            :placeholder="
              t(
                `WGPT_HUBPOST_CARS_SHOWROOM_SETUP.CONTACT.${field.key}_PLACEHOLDER`
              )
            "
            custom-input-class="w-auto text-end px-1 py-0.5 -my-0.5 mx-0 placeholder:text-n-slate-9 rounded"
          />
        </OnboardingFormRow>
      </OnboardingSection>

      <OnboardingSection
        :title="t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.SOCIAL.TITLE')"
        icon="i-lucide-share-2"
      >
        <OnboardingFormRow
          v-for="platform in SOCIAL_PLATFORMS"
          :key="platform"
          :title="t(`WGPT_HUBPOST_CARS_SHOWROOM_SETUP.SOCIAL.${platform}`)"
          :icon="SOCIAL_ICONS[platform]"
        >
          <InlineInput
            v-model="socialLinks[platform]"
            :placeholder="
              t('WGPT_HUBPOST_CARS_SHOWROOM_SETUP.SOCIAL.PLACEHOLDER')
            "
            custom-input-class="w-auto text-end px-1 py-0.5 -my-0.5 mx-0 placeholder:text-n-slate-9 rounded"
          />
        </OnboardingFormRow>
      </OnboardingSection>
    </OnboardingLayout>
  </form>
</template>
